// src/component/PreviewTopPageMusicSection.js
// 擬似トップページ用：TopPageMusicSection.js のコピー
// 本家とは独立して編集可能
// 依存: react, @mui/material, firebase v9
//
// 目的:
// 1) 現在の人数（今この瞬間に聴いている人）
//    - presence minutes aggregates（分単位集計doc）を windowMin 分ぶん getDoc して合算（読み取り最適化）
// 2) 今日のユニーク（今日『音』を繋いだ人数）は、musicDailyAggregates の 1doc getDoc で読む（読み取り最適化）
//
// NOTE:
// - listening 制御は appFlags/musicListeningControl の4値を「presence read」にも相乗りします。
//   (phase4PollSec/phase4WindowMin/nonPhase4PollSec/nonPhase4WindowMin)
//
// - Functions 側:
//   - raw: musicPresenceMinutes/{docId} onCreate
//   - agg: musicCurrentAggregatesMinute/{trackId}_{minuteKey} に count++
//
// 実装方針:
// - 書き込み（presence）は 4期 & 再生中のみ。間隔は固定（PRESENCE_WRITE_SEC）
// - 読み取り（現在人数）は appFlag の pollSec/windowMin に相乗り
// - 非4期は pollSec=0/windowMin=0 を推奨（=単発取得 or 0扱い）

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Slider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@mui/material";

import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import LoopRoundedIcon from "@mui/icons-material/LoopRounded";
import SelfImprovementRoundedIcon from "@mui/icons-material/SelfImprovementRounded";

import { AppThemeContext } from "./AppShell";
import { useAuth } from "../context/auth";
import { db } from "../api/firebase";

import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
  setDoc,
  serverTimestamp,
  addDoc,
  getCountFromServer,
  Timestamp,
  onSnapshot,
  limit,
} from "firebase/firestore";

import MusicGeoPrefectureStats from "./PreviewMusicGeoPrefectureStats";

import Badge from "@mui/material/Badge";
import CommunityFeedNoImage from "./PreviewCommunityFeedNoImage";
import OtohikariGlobe from "./PreviewOtohikariGlobe";
import { useDayBoundary } from "../context/DayBoundaryContext";

// ─────────────────────────────────────────────
// スイッチ類
// ─────────────────────────────────────────────
// ENABLE_PLAYBACK_LOG は appFlags/musicPlaybackErrorLog.enabled で動的に制御
const ENABLE_LISTENING_DEBUG = true; // Listening のデバッグUI（管理者のみ）ON/OFF

// ★ 今この瞬間 を presence aggregates で読む
const USE_PRESENCE_MINUTES_AGG = true;

// ★ presence heartbeat 書き込み間隔（これは appFlag に相乗りさせない方が安全）
const PRESENCE_WRITE_SEC = 10;

// ★ Functions 側に合わせたコレクション名
const PRESENCE_MINUTES_RAW_COL = "musicPresenceMinutes"; // onCreate のトリガー元
const PRESENCE_AGG_MINUTE_COL = "musicCurrentAggregatesMinute"; // 集計結果

// ★ listening 制御の既定値（DBが無い/欠けてる時のフォールバック）
const DEFAULT_LISTENING_CTRL = {
  phase4PollSec: 5, // 4期: 5秒
  phase4WindowMin: 10, // 4期: 10分
  nonPhase4PollSec: 0, // 非4期: 0=更新なし（単発のみ）
  nonPhase4WindowMin: 0,
};

// ★ 管理者UIを見せたくないUID
const ADMIN_UI_DENY_UIDS = new Set([
  // "HofNkgoUJoQUefrYyKUu6GB3Ppk1",
  // "Gxf2Oq0WnhehnoMIPz1H9k5NURk1",
]);

// ─────────────────────────────────────────────
// 表示用タイトル（拡張子を隠す）
// ─────────────────────────────────────────────
const getDisplayTitle = (track) => {
  if (!track) return "";
  const raw = track.name || track.fileName || "";
  return raw.replace(/\.(mp3|wav|m4a|flac|aac)$/i, "");
};

// ─────────────────────────────────────────────
// URL 解決
// ─────────────────────────────────────────────
const resolveAudioSrc = (track) => {
  if (!track) return null;

  if (track.fileName && typeof track.fileName === "string") {
    return `/audio/${track.fileName}`;
  }

  if (track.url && typeof track.url === "string") {
    if (
      track.url.startsWith("http://") ||
      track.url.startsWith("https://") ||
      track.url.startsWith("/audio/")
    ) {
      return track.url;
    }
    return `/audio/${track.url}`;
  }

  if (track.name && typeof track.name === "string") {
    return `/audio/${track.name}`;
  }

  return null;
};

// allowedTerm / userTerm 用簡易 normalize
const normalizeTerms = (value) => {
  if (Array.isArray(value)) {
    return value.map((v) => String(v).trim()).filter(Boolean);
  }
  if (value === undefined || value === null) return [];
  return [String(value).trim()].filter(Boolean);
};

// buildDateKey は DayBoundaryContext 経由で取得（コンポーネント内で useDayBoundary()）

// presence minutes 用キー: "YYYY-MM-DD_HH:mm"（分単位）
const buildMinuteKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const mm = String(date.getMinutes()).padStart(2, "0");
  return `${y}-${m}-${d}_${hh}:${mm}`;
};

// 直近 windowMin 分の minuteKey 配列（現在分を含む）
const buildMinuteKeys = (windowMin) => {
  const n = Math.max(0, Math.floor(windowMin || 0));
  if (n <= 0) return [];
  const now = new Date();
  now.setSeconds(0, 0);

  const keys = [];
  for (let i = 0; i < n; i++) {
    const dt = new Date(now.getTime() - i * 60 * 1000);
    keys.push(buildMinuteKey(dt));
  }
  return keys; // 新しい順
};

// 数値安全化
const toInt = (v, fallback) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return Math.floor(n);
};

// ─────────────────────────────────────────────
// 「全量ダウンロード → Blob → ローカル再生」用
// ─────────────────────────────────────────────
const AUDIO_CACHE_NAME = "mm-audio-cache-v1";

const safeRevokeObjectURL = (url) => {
  try {
    if (url && typeof url === "string" && url.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  } catch (_) {}
};

const getFileNameFromSrc = (src) => {
  if (!src) return "";
  try {
    const path = src.split("?")[0];
    const parts = path.split("/");
    return parts[parts.length - 1] || "";
  } catch {
    return "";
  }
};

const formatMB = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return "0.00MB";
  return `${(bytes / (1024 * 1024)).toFixed(2)}MB`;
};

const buildCacheKeyString = (track, src) => {
  return `mm_audio_${track?.id || "unknown"}_${src || ""}`;
};

// 音源ファイル未デプロイ時に SPA リライトで index.html が 200 で返るため、
// HTML 応答・極端に小さい応答は「壊れた音源」として弾く
const MIN_VALID_AUDIO_BYTES = 100 * 1024;
const looksLikeHtml = (contentType) => /text\/html/i.test(contentType || "");
const isValidAudioBlob = (blob, contentType) =>
  !looksLikeHtml(contentType || blob?.type) &&
  (blob?.size || 0) >= MIN_VALID_AUDIO_BYTES;

const downloadToLocalBlob = async ({
  url,
  cacheKeyString,
  signal,
  onProgress,
}) => {
  const canUseCache =
    typeof caches !== "undefined" && typeof caches.open === "function";

  // Cache Storage にあればそれを使う（壊れたエントリは削除してネットワークへ）
  if (canUseCache) {
    try {
      const cache = await caches.open(AUDIO_CACHE_NAME);
      const cached = await cache.match(cacheKeyString);
      if (cached) {
        const cachedType = cached.headers.get("content-type") || "";
        const blob = await cached.blob();
        if (isValidAudioBlob(blob, cachedType)) {
          return { blob, fromCache: true, total: blob.size };
        }
        console.warn("Broken audio cache entry, refetching:", cacheKeyString);
        try {
          await cache.delete(cacheKeyString);
        } catch (_) {}
      }
    } catch (e) {
      console.warn("Cache match failed, fallback to network:", e);
    }
  }

  // no-store: /audio/** には immutable が付いているため、フォールバック HTML が
  // HTTP キャッシュに残ると通常 fetch では永久にそれを拾ってしまう
  const res = await fetch(url, { signal, cache: "no-store" });
  if (!res.ok) throw new Error(`fetch failed: ${res.status}`);

  const total = Number(res.headers.get("content-length")) || 0;
  const contentType = res.headers.get("content-type") || "audio/mpeg";
  if (looksLikeHtml(contentType)) {
    throw new Error("audio not deployed yet (HTML fallback response)");
  }

  const putValidBlobToCache = async (blob) => {
    if (!canUseCache) return;
    try {
      const cache = await caches.open(AUDIO_CACHE_NAME);
      await cache.put(
        cacheKeyString,
        new Response(blob, {
          headers: { "content-type": blob.type || contentType },
        })
      );
    } catch (e) {
      console.warn("Cache put failed:", e);
    }
  };

  // ストリームが取れない環境は一括
  if (!res.body || typeof res.body.getReader !== "function") {
    const blob = await res.blob();
    if (!isValidAudioBlob(blob, contentType)) {
      throw new Error(`invalid audio response (${blob.size} bytes)`);
    }
    await putValidBlobToCache(blob);
    return { blob, fromCache: false, total: blob.size || total };
  }

  const reader = res.body.getReader();
  let received = 0;
  const chunks = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    chunks.push(value);
    received += value.byteLength;
    if (onProgress && total > 0) onProgress(received, total);
  }

  const blob = new Blob(chunks, { type: contentType });
  if (!isValidAudioBlob(blob, contentType)) {
    throw new Error(`invalid audio response (${blob.size} bytes)`);
  }

  await putValidBlobToCache(blob);

  return { blob, fromCache: false, total: total || blob.size };
};

// ─────────────────────────────────────────────
// 再生異常ログ（任意）
// ─────────────────────────────────────────────
const logPlaybackIssue = async ({ event, audio, user, track, extra, enabled }) => {
  if (!enabled) return;

  try {
    const state = audio
      ? {
          currentTime: audio.currentTime,
          duration: audio.duration,
          paused: audio.paused,
          ended: audio.ended,
          readyState: audio.readyState,
          networkState: audio.networkState,
          currentSrc: audio.currentSrc || null,
        }
      : null;

    await addDoc(collection(db, "error"), {
      type: "musicPlayback",
      event,
      trackId: track?.id || null,
      trackName: track?.name || null,
      userId: user?.uid || null,
      state,
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      extra: extra || null,
      createdAt: serverTimestamp(),
    });
  } catch (e) {
    console.error("Failed to log playback issue:", e);
  }
};

// ─────────────────────────────
// バースト：windowMs の間だけ polling を動かして停止
// ─────────────────────────────
function useBurstScheduler({ enabled, windowMs }) {
  const runningRef = useRef(false);
  const stopTimerRef = useRef(null);

  const startBurst = React.useCallback(
    (fnStart, fnStop) => {
      if (!enabled) return;
      if (runningRef.current) return;

      runningRef.current = true;
      fnStart?.();

      if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
      stopTimerRef.current = setTimeout(() => {
        runningRef.current = false;
        fnStop?.();
      }, windowMs);
    },
    [enabled, windowMs]
  );

  const forceStop = React.useCallback((fnStop) => {
    if (stopTimerRef.current) clearTimeout(stopTimerRef.current);
    stopTimerRef.current = null;
    runningRef.current = false;
    fnStop?.();
  }, []);

  useEffect(() => {
    if (!enabled) forceStop();
    return () => forceStop();
  }, [enabled, forceStop]);

  return useMemo(
    () => ({ startBurst, forceStop, isRunningRef: runningRef, windowMs }),
    [startBurst, forceStop, windowMs]
  );
}

// mm:ss
const formatMMSS = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const ss = String(s % 60).padStart(2, "0");
  return `${m}:${ss}`;
};

// ─────────────────────────────────────────────
// TopPageMusicSection
// ─────────────────────────────────────────────
export default function TopPageMusicSection() {
  const theme = useContext(AppThemeContext) || {};
  const accent = theme.railActive || "#5A35FF";
  const softBg = theme.surfaceBg || "#FFFFFF";
  const border = theme.bottomBorder || "rgba(0,0,0,0.06)";
  const textColor = theme.railText || "rgba(40,40,60,0.9)";

  const { user } = useAuth();
  const { buildDateKey } = useDayBoundary();

  const [track, setTrack] = useState(null);
  const [loadingTrack, setLoadingTrack] = useState(true);

  const [userTerms, setUserTerms] = useState(null);
  const [userPrefecture, setUserPrefecture] = useState(null);
  const [userCountry, setUserCountry] = useState(null);
  const [userLat, setUserLat] = useState(null);
  const [userLng, setUserLng] = useState(null);
  const [loadingUserTerms, setLoadingUserTerms] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // ★ 実効admin（isAdmin でも特定UIDは除外）
  const canSeeAdminUI = useMemo(() => {
    const uid = user?.uid || "";
    if (!uid) return false;
    if (ADMIN_UI_DENY_UIDS.has(uid)) return false;
    return Boolean(isAdmin);
  }, [user?.uid, isAdmin]);

  // 4期ユーザ判定（更新権限）
  const isPhase4 = useMemo(() => {
    if (loadingUserTerms) return false;
    return Array.isArray(userTerms) && userTerms.includes("4");
  }, [userTerms, loadingUserTerms]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoop, setIsLoop] = useState(false);

  // 親は duration のみ保持（currentTimeは子に隔離）
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  // 瞑想モード
  const [meditationMode, setMeditationMode] = useState(false);
  const wakeLockRef = useRef(null);

  // 権限エラーダイアログ
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);

  // リスナー数
  const [currentListeners, setCurrentListeners] = useState(0);
  const [todayListeners, setTodayListeners] = useState(0);
  const [showDlInfo, setShowDlInfo] = useState(false);

  // 今日キー
  const [todayKey, setTodayKey] = useState(buildDateKey(new Date()));

  // Map 更新トークン（既存通り）
  const [mapRefreshToken, setMapRefreshToken] = useState(0);
  const refreshMapNow = React.useCallback(() => {
    setMapRefreshToken((v) => v + 1);
  }, []);

  // ★ 音楽再生エラーログ ON/OFF（appFlags/musicPlaybackErrorLog）
  const [playbackErrorLogEnabled, setPlaybackErrorLogEnabled] = useState(false);

  useEffect(() => {
    const ref = doc(db, "appFlags", "musicPlaybackErrorLog");
    const unsub = onSnapshot(
      ref,
      (snap) => {
        setPlaybackErrorLogEnabled(snap.exists() ? Boolean(snap.data()?.enabled) : false);
      },
      () => setPlaybackErrorLogEnabled(false)
    );
    return () => unsub();
  }, []);

  // ★ listening control（Firestore）
  const [listeningCtrl, setListeningCtrl] = useState(DEFAULT_LISTENING_CTRL);
  const [listeningCtrlReady, setListeningCtrlReady] = useState(false);

  const [feedVisibility, setFeedVisibility] = useState(null);
  const [loadingFeedVisibility, setLoadingFeedVisibility] = useState(true);

  useEffect(() => {
    const ref = doc(db, "appFlags", "communityFeedVisibility");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        setFeedVisibility(snap.exists() ? snap.data() : null);
        setLoadingFeedVisibility(false);
      },
      () => {
        setFeedVisibility(null);
        setLoadingFeedVisibility(false);
      }
    );

    return () => unsub();
  }, []);

  const canSeeCommunityFeed = useMemo(() => {
    if (loadingFeedVisibility) return false;
    if (!feedVisibility?.enabled) return false;

    // 管理者
    if (feedVisibility.allowAdmins && canSeeAdminUI) {
      return true;
    }

    // 支払い期
    const allowedPhases = feedVisibility.allowPaymentPhases || [];
    if (allowedPhases.length === 0) return false;

    const userPhases = Array.isArray(userTerms) ? userTerms : [];
    return userPhases.some((p) => allowedPhases.includes(String(p)));
  }, [loadingFeedVisibility, feedVisibility, canSeeAdminUI, userTerms]);

  useEffect(() => {
    const ref = doc(db, "appFlags", "musicListeningControl");

    const unsub = onSnapshot(
      ref,
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        const next = {
          phase4PollSec:
            typeof data.phase4PollSec === "number"
              ? data.phase4PollSec
              : DEFAULT_LISTENING_CTRL.phase4PollSec,
          phase4WindowMin:
            typeof data.phase4WindowMin === "number"
              ? data.phase4WindowMin
              : DEFAULT_LISTENING_CTRL.phase4WindowMin,
          nonPhase4PollSec:
            typeof data.nonPhase4PollSec === "number"
              ? data.nonPhase4PollSec
              : DEFAULT_LISTENING_CTRL.nonPhase4PollSec,
          nonPhase4WindowMin:
            typeof data.nonPhase4WindowMin === "number"
              ? data.nonPhase4WindowMin
              : DEFAULT_LISTENING_CTRL.nonPhase4WindowMin,
        };

        setListeningCtrl(next);
        setListeningCtrlReady(true);
      },
      (err) => {
        console.warn("musicListeningControl onSnapshot error:", err);
        setListeningCtrl(DEFAULT_LISTENING_CTRL);
        setListeningCtrlReady(true);
      }
    );

    return () => unsub();
  }, []);

  // ★ derived listening config（appFlag 4値を presence read に相乗り）
  const derivedListening = useMemo(() => {
    const pollSec = isPhase4
      ? toInt(listeningCtrl.phase4PollSec, DEFAULT_LISTENING_CTRL.phase4PollSec)
      : toInt(
          listeningCtrl.nonPhase4PollSec,
          DEFAULT_LISTENING_CTRL.nonPhase4PollSec
        );

    const windowMin = isPhase4
      ? toInt(
          listeningCtrl.phase4WindowMin,
          DEFAULT_LISTENING_CTRL.phase4WindowMin
        )
      : toInt(
          listeningCtrl.nonPhase4WindowMin,
          DEFAULT_LISTENING_CTRL.nonPhase4WindowMin
        );

    const shouldPoll = pollSec > 0 && windowMin > 0;
    const windowMs = Math.max(0, windowMin) * 60 * 1000;

    return { pollSec, windowMin, shouldPoll, windowMs };
  }, [isPhase4, listeningCtrl]);

  // ★ listening burst (Hookはコンポーネント直下で呼ぶ)
  const listeningBurst = useBurstScheduler({
    enabled: Boolean(track?.id) && derivedListening.shouldPoll,
    windowMs: derivedListening.windowMs || 0,
  });

  // ★ map burst（従来通り固定10分）
  const mapBurst = useBurstScheduler({
    enabled: Boolean(track?.id) && isPhase4,
    windowMs: 10 * 60 * 1000,
  });

  // ★ 管理者だけ：Listening更新タイマー表示
  const [listeningEndAtMs, setListeningEndAtMs] = useState(null); // null=停止中
  const [listeningRemainSec, setListeningRemainSec] = useState(0);

  // ★ 管理者だけ：Listening debug
  const [lsDebug, setLsDebug] = useState(null);

  const listeningActive = Boolean(listeningEndAtMs && listeningRemainSec > 0);

  useEffect(() => {
    if (!listeningEndAtMs) {
      setListeningRemainSec(0);
      return;
    }

    const tick = () => {
      const remain = Math.max(
        0,
        Math.ceil((listeningEndAtMs - Date.now()) / 1000)
      );
      setListeningRemainSec(remain);
      if (remain <= 0) setListeningEndAtMs(null);
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [listeningEndAtMs]);

  // ─────────────────────────────
  // Map更新（バースト）
  // ─────────────────────────────
  useEffect(() => {
    if (!track?.id) return;

    let alive = true;
    let intervalId = null;

    const tickMap = () => {
      if (!alive) return;
      refreshMapNow();
    };

    const startMapPolling = () => {
      tickMap();
      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(tickMap, 60 * 1000);
    };

    const stopMapPolling = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = null;
    };

    if (isPhase4) {
      mapBurst.startBurst(startMapPolling, stopMapPolling);
    } else {
      tickMap();
    }

    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (isPhase4) mapBurst.startBurst(startMapPolling, stopMapPolling);
      else tickMap();
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVis);
      stopMapPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track?.id, isPhase4, refreshMapNow, mapBurst]);

  // 全量DL
  const networkSrc = resolveAudioSrc(track);

  const [localSrc, setLocalSrc] = useState(null);
  const [dlState, setDlState] = useState({
    status: "idle", // idle | downloading | ready | error
    progress: 0,
    loaded: 0,
    total: 0,
    message: "",
    fromCache: false,
  });

  useEffect(() => {
    if (!track?.id || !networkSrc) {
      setDlState({
        status: "idle",
        progress: 0,
        loaded: 0,
        total: 0,
        message: "",
        fromCache: false,
      });
      setLocalSrc((prev) => {
        safeRevokeObjectURL(prev);
        return null;
      });
      return;
    }

    // 曲が差し替わったら止める
    try {
      const a = audioRef.current;
      if (a && !a.paused) a.pause();
    } catch (_) {}
    setIsPlaying(false);
    setDuration(0);

    setLocalSrc((prev) => {
      safeRevokeObjectURL(prev);
      return null;
    });

    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        const cacheKeyString = buildCacheKeyString(track, networkSrc);

        setDlState({
          status: "downloading",
          progress: 0,
          loaded: 0,
          total: 0,
          message: "音源をダウンロードしています…",
          fromCache: false,
        });

        const { blob, fromCache, total } = await downloadToLocalBlob({
          url: networkSrc,
          cacheKeyString,
          signal: controller.signal,
          onProgress: (loaded, totalBytes) => {
            if (cancelled) return;
            const p = Math.min(100, Math.round((loaded / totalBytes) * 100));
            setDlState({
              status: "downloading",
              progress: p,
              loaded,
              total: totalBytes,
              message: "音源をダウンロードしています…",
              fromCache: false,
            });
          },
        });

        if (cancelled) return;

        const blobUrl = URL.createObjectURL(blob);
        setLocalSrc(blobUrl);

        setDlState({
          status: "ready",
          progress: 100,
          loaded: blob.size,
          total: total || blob.size,
          message: "",
          fromCache: !!fromCache,
        });
      } catch (e) {
        if (e?.name === "AbortError") return;
        console.error("audio predownload failed:", e);
        if (cancelled) return;

        setLocalSrc(null);
        setDlState({
          status: "error",
          progress: 0,
          loaded: 0,
          total: 0,
          message: "音源の準備に失敗しました。通信環境をご確認ください。",
          fromCache: false,
        });

        logPlaybackIssue({
          event: "predownload_error",
          audio: audioRef.current,
          user,
          track,
          extra: { message: String(e?.message || e), src: networkSrc },
          enabled: playbackErrorLogEnabled,
        });
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [track?.id, networkSrc, user, track]);

  const playableSrc = dlState.status === "ready" ? localSrc : null;
  const canPlay = Boolean(playableSrc) && dlState.status === "ready";

  const adminDlFileName =
    track?.fileName || getFileNameFromSrc(networkSrc) || "";
  const adminDlSizeBytes = dlState?.loaded || dlState?.total || 0;

  // ─────────────────────────────
  // TopPage用トラック取得
  // ─────────────────────────────
  useEffect(() => {
    const fetchTrack = async () => {
      try {
        const settingsRef = doc(db, "musicSettings", "topPageSeasonalTrack");
        const settingsSnap = await getDoc(settingsRef);
        if (!settingsSnap.exists()) {
          setTrack(null);
          return;
        }

        const settings = settingsSnap.data();
        if (!settings.trackId) {
          setTrack(null);
          return;
        }

        const trackRef = doc(db, "tracks", settings.trackId);
        const trackSnap = await getDoc(trackRef);

        if (trackSnap.exists()) {
          const data = trackSnap.data();
          setTrack({ id: trackSnap.id, ...data });
        } else {
          setTrack({
            id: settings.trackId,
            name: settings.title || "",
            creator: settings.creator || "",
            url: settings.src || "",
            seasonSlot: settings.seasonSlot || null,
            allowedTerm: [],
          });
        }
      } catch (e) {
        console.error("Failed to load topPageSeasonalTrack:", e);
        setTrack(null);
      } finally {
        setLoadingTrack(false);
      }
    };

    fetchTrack();
  }, []);

  // ─────────────────────────────
  // ユーザーの支払い期 & prefecture & isAdmin 取得（リアルタイム）
  // ─────────────────────────────
  useEffect(() => {
    if (!user) {
      setUserTerms([]);
      setUserPrefecture(null);
      setUserCountry(null);
      setUserLat(null);
      setUserLng(null);
      setIsAdmin(false);
      setLoadingUserTerms(false);
      return;
    }

    const userRef = doc(db, "users", user.uid);

    const unsubscribe = onSnapshot(
      userRef,
      (userSnap) => {
        if (!userSnap.exists()) {
          setUserTerms([]);
          setUserPrefecture(null);
          setUserCountry(null);
          setUserLat(null);
          setUserLng(null);
          setIsAdmin(false);
          setLoadingUserTerms(false);
          return;
        }

        const data = userSnap.data();
        const payments = Array.isArray(data.payments) ? data.payments : [];
        const paymentPhases = payments
          .map((p) => String(p?.paymentPhase ?? "").trim())
          .filter((v) => v);

        setUserTerms(paymentPhases);
        setUserPrefecture(data.prefecture || null);
        setUserCountry(data.country || null);
        setUserLat(typeof data.lat === "number" ? data.lat : null);
        setUserLng(typeof data.lng === "number" ? data.lng : null);
        setIsAdmin(Boolean(data.isAdmin));
        setLoadingUserTerms(false);
      },
      (e) => {
        console.error("Failed to listen user doc:", e);
        setUserTerms([]);
        setUserPrefecture(null);
        setUserCountry(null);
        setUserLat(null);
        setUserLng(null);
        setIsAdmin(false);
        setLoadingUserTerms(false);
      }
    );

    return () => unsubscribe();
  }, [user]);

  // ─────────────────────────────
  // Presence heartbeat（4期 & 再生中のみ）
  //  - Functions の onCreate が minuteKey を読むので必ず入れる
  //  - docId はユニークであれば良い（ここでは trackId_minuteKey_userId）
  // ─────────────────────────────
  useEffect(() => {
    if (!user || !track?.id) return;
    if (!isPhase4) return;
    if (!isPlaying) return;

    let timerId = null;
    let cancelled = false;

    const writeOnce = async () => {
      try {
        if (cancelled) return;

        // バックグラウンドでは書かない（無駄な書き込みと誤差防止）
        if (typeof document !== "undefined") {
          if (document.visibilityState !== "visible") return;
        }

        const minuteKey = buildMinuteKey(new Date());
        const docId = `${track.id}_${minuteKey}_${user.uid}`;

        await setDoc(
          doc(db, PRESENCE_MINUTES_RAW_COL, docId),
          {
            trackId: track.id,
            minuteKey, // ★必須
            userId: user.uid,
            updatedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.warn("presence write failed:", e?.code || "", e?.message || e);
      }
    };

    writeOnce();
    timerId = setInterval(writeOnce, PRESENCE_WRITE_SEC * 1000);

    return () => {
      cancelled = true;
      if (timerId) clearInterval(timerId);
    };
  }, [user?.uid, track?.id, isPhase4, isPlaying]);

  // ─────────────────────────────
  // LISTENING STATUS（DB制御）
  //  - 現在の人数:
  //      presence aggregates: musicCurrentAggregatesMinute/{trackId}_{minuteKey} を windowMin 分合算
  //      ※nonPhase4 で windowMin=0 は 0扱い（読まない）
  //  - 今日のユニーク:
  //      musicDailyAggregates/{trackId}_{todayKey} を getDoc
  // ─────────────────────────────
  useEffect(() => {
    if (!track?.id || !todayKey) return;
    if (!listeningCtrlReady) return;

    let alive = true;
    let intervalId = null;

    const pollSec = derivedListening.pollSec;
    const windowMin = derivedListening.windowMin;
    const shouldPoll = derivedListening.shouldPoll;
    const windowMs = derivedListening.windowMs;

    const fetchBoth = async ({ force = false } = {}) => {
      if (!alive) return;

      if (
        typeof document !== "undefined" &&
        document.visibilityState !== "visible"
      ) {
        if (!force) return;
      }

      // 旧方式用 threshold（残しておく：デバッグ/切り戻し用）
      const now = Date.now();
      const thresholdMs = now - 60 * 1000;
      const threshold = Timestamp.fromMillis(thresholdMs);

      try {
        // ─────────────
        // 1) 現在の人数
        // ─────────────
        let cur = 0;

        if (USE_PRESENCE_MINUTES_AGG) {
          const minuteKeys = buildMinuteKeys(windowMin);

          // 非4期で windowMin=0 の場合は「読まない（=0）」運用
          if (!isPhase4 && minuteKeys.length === 0) {
            cur = 0;
          } else {
            let sum = 0;
            for (const k of minuteKeys) {
              const aggId = `${track.id}_${k}`;
              const aggRef = doc(db, PRESENCE_AGG_MINUTE_COL, aggId);
              const aggSnap = await getDoc(aggRef);
              if (aggSnap.exists()) {
                const d = aggSnap.data() || {};
                sum += typeof d.count === "number" ? d.count : 0;
              }
            }
            cur = sum;
          }
        } else {
          // 旧方式（現行維持）
          const qCurrent = query(
            collection(db, "musicListeningSessions"),
            where("trackId", "==", track.id),
            where("lastActiveAt", ">=", threshold)
          );

          const c1 = await getCountFromServer(qCurrent);
          if (!alive) return;
          cur = c1.data().count || 0;
        }

        // ─────────────
        // 2) 今日『音』を繋いだ人数（1doc）
        // ─────────────
        let tod = 0;
        try {
          const aggRef = doc(
            db,
            "musicDailyAggregates",
            `${track.id}_${todayKey}`
          );
          const aggSnap = await getDoc(aggRef);
          if (aggSnap.exists()) {
            const d = aggSnap.data() || {};
            tod = typeof d.count === "number" ? d.count : 0;
          }
        } catch (e) {
          console.warn(
            "Agg read failed (todayListeners):",
            e?.code || "",
            e?.message || e
          );
          tod = 0;
        }

        setCurrentListeners(cur);
        setTodayListeners(tod);

        if (ENABLE_LISTENING_DEBUG && canSeeAdminUI) {
          let sampleCurrentIds = [];
          if (!USE_PRESENCE_MINUTES_AGG) {
            try {
              const qCurrent = query(
                collection(db, "musicListeningSessions"),
                where("trackId", "==", track.id),
                where("lastActiveAt", ">=", threshold),
                limit(3)
              );
              const s1 = await getDocs(qCurrent);
              sampleCurrentIds = s1.docs.map((d) => d.id);
            } catch (_) {}
          }

          const minuteKeys = USE_PRESENCE_MINUTES_AGG
            ? buildMinuteKeys(windowMin)
            : null;

          const debugObj = {
            ctrl: {
              pollSec,
              windowMin,
              shouldPoll,
              usePresenceAgg: USE_PRESENCE_MINUTES_AGG,
            },
            trackId: track?.id || null,
            todayKey,
            thresholdISO: new Date(thresholdMs).toISOString(),
            minuteKeys,
            counts: { current: cur, todayAgg: tod },
            sampleCurrent: sampleCurrentIds,
            ts: new Date().toISOString(),
          };
          setLsDebug(debugObj);
          // console.warn("[LISTENING_DEBUG]", debugObj);
        }
      } catch (e) {
        const code = e?.code || "unknown";
        const msg = e?.message || String(e);

        console.warn("LISTENING COUNT ERROR:", code, msg);

        if (ENABLE_LISTENING_DEBUG && canSeeAdminUI) {
          setLsDebug({
            ctrl: {
              pollSec,
              windowMin,
              shouldPoll,
              usePresenceAgg: USE_PRESENCE_MINUTES_AGG,
            },
            trackId: track?.id || null,
            todayKey,
            thresholdISO: new Date(Date.now() - 60 * 1000).toISOString(),
            error: { code, msg },
            ts: new Date().toISOString(),
          });
        }
      }
    };

    const stopPolling = () => {
      if (intervalId) clearInterval(intervalId);
      intervalId = null;

      if (canSeeAdminUI) setListeningEndAtMs(null);
    };

    const startPolling = () => {
      if (canSeeAdminUI) setListeningEndAtMs(Date.now() + windowMs);

      fetchBoth({ force: true });

      if (intervalId) clearInterval(intervalId);
      intervalId = setInterval(fetchBoth, Math.max(1, pollSec) * 1000);
    };

    if (shouldPoll) {
      listeningBurst.startBurst(startPolling, stopPolling);
    } else {
      fetchBoth({ force: true });
      if (canSeeAdminUI) setListeningEndAtMs(null);
    }

    const onVis = () => {
      if (document.visibilityState !== "visible") return;
      if (shouldPoll) listeningBurst.startBurst(startPolling, stopPolling);
      else fetchBoth({ force: true });
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVis);
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    track?.id,
    todayKey,
    listeningCtrlReady,
    derivedListening.pollSec,
    derivedListening.windowMin,
    derivedListening.shouldPoll,
    derivedListening.windowMs,
    canSeeAdminUI,
    isPhase4,
  ]);

  // ─────────────────────────────
  // 日付の変化を監視して todayKey を更新
  // ─────────────────────────────
  useEffect(() => {
    const checkDate = () => {
      const k = buildDateKey(new Date());
      setTodayKey((prev) => (prev === k ? prev : k));
    };

    checkDate();
    const id = setInterval(checkDate, 60 * 1000);
    return () => clearInterval(id);
  }, []);

  // ─────────────────────────────
  // セッション管理 & 日次ユニーク+prefecture保存（再生中のみ）
  // ─────────────────────────────
  useEffect(() => {
    if (!user || !track?.id) return;

    let intervalId;
    const sessionRef = doc(
      db,
      "musicListeningSessions",
      `${user.uid}_${track.id}`
    );

    const touchSession = async () => {
      try {
        await setDoc(
          sessionRef,
          {
            userId: user.uid,
            trackId: track.id,
            lastActiveAt: serverTimestamp(),
            startedAt: serverTimestamp(),
          },
          { merge: true }
        );
      } catch (e) {
        console.error("Failed to update listening session:", e);
      }
    };

    const ensureDailyPlayDoc = async () => {
      try {
        const todayKeyNow = buildDateKey(new Date());
        const dailyRef = doc(
          db,
          "musicDailyUserPlays",
          `${track.id}_${todayKeyNow}_${user.uid}`
        );

        const snap = await getDoc(dailyRef);
        if (!snap.exists()) {
          await setDoc(dailyRef, {
            userId: user.uid,
            trackId: track.id,
            dateKey: todayKeyNow,
            prefecture: userPrefecture || null,
            country: userCountry || null,
            lat: userLat,
            lng: userLng,
            firstPlayedAt: serverTimestamp(),
          });
        } else if (
          (!snap.data().prefecture && userPrefecture) ||
          (!snap.data().country && userCountry) ||
          (snap.data().lat == null && userLat != null)
        ) {
          await setDoc(
            dailyRef,
            {
              prefecture: userPrefecture || null,
              country: userCountry || null,
              lat: userLat,
              lng: userLng,
            },
            { merge: true }
          );
        }
      } catch (e) {
        console.error("Failed to record daily play:", e);
      }
    };

    if (isPlaying) {
      touchSession();
      ensureDailyPlayDoc();

      intervalId = setInterval(() => {
        touchSession();
      }, 45000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isPlaying, user?.uid, track?.id, userPrefecture, userCountry, userLat, userLng]);
  // ─────────────────────────────
  // Wake Lock（瞑想モード用）
  // ─────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const requestWakeLock = async () => {
      if (typeof navigator === "undefined") return;
      if (!("wakeLock" in navigator)) return;

      try {
        const lock = await navigator.wakeLock.request("screen");
        if (cancelled) {
          await lock.release().catch(() => {});
          return;
        }
        wakeLockRef.current = lock;
      } catch (err) {
        console.error("Wake Lock error:", err);
      }
    };

    const releaseWakeLock = async () => {
      if (wakeLockRef.current) {
        try {
          await wakeLockRef.current.release();
        } catch (e) {
          console.error("Failed to release Wake Lock", e);
        } finally {
          wakeLockRef.current = null;
        }
      }
    };

    if (meditationMode) requestWakeLock();
    else releaseWakeLock();

    return () => {
      cancelled = true;
      releaseWakeLock();
    };
  }, [meditationMode]);

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (
        document.visibilityState === "visible" &&
        meditationMode &&
        "wakeLock" in navigator
      ) {
        try {
          const lock = await navigator.wakeLock.request("screen");
          wakeLockRef.current = lock;
        } catch (e) {
          console.error("Wake Lock re-acquire failed:", e);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [meditationMode]);

  // ─────────────────────────────
  // 再生権限チェック
  // ─────────────────────────────
  const hasPlayPermission = () => {
    if (!track) return false;
    const trackAllowed = normalizeTerms(track.allowedTerm);

    if (trackAllowed.length === 0) return true;
    if (loadingUserTerms) return false;

    const userSet = new Set((userTerms || []).map((t) => String(t)));
    return trackAllowed.some((t) => userSet.has(String(t)));
  };

  // ─────────────────────────────
  // 再生 / 一時停止
  // ─────────────────────────────
  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio || !playableSrc) {
      logPlaybackIssue({
        event: "play_not_ready",
        audio: null,
        user,
        track,
        extra: {
          message: !audio
            ? "audio要素が存在しない"
            : "playableSrcが未準備（ダウンロード未完了または失敗）",
          dlStatus: dlState.status,
          dlMessage: dlState.message,
          hasAudio: Boolean(audio),
          hasPlayableSrc: Boolean(playableSrc),
          networkSrc: networkSrc || null,
        },
        enabled: playbackErrorLogEnabled,
      });
      return;
    }

    if (!hasPlayPermission()) {
      setPermissionDialogOpen(true);
      return;
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      return;
    }

    const timeBeforePlay = audio.currentTime;

    audio
      .play()
      .then(() => {
        setIsPlaying(true);

        // Map更新は 8秒後（再生ボタン直後の詰まり回避）
        window.setTimeout(() => {
          if (isPhase4) refreshMapNow();
        }, 8000);

        // 3秒後に再生が進んでいるかチェック（サイレント失敗検知）
        window.setTimeout(() => {
          const a = audioRef.current;
          if (a && !a.paused && a.currentTime === timeBeforePlay) {
            logPlaybackIssue({
              event: "play_silent_failure",
              audio: a,
              user,
              track,
              extra: {
                message: "再生開始後3秒経過しても currentTime が進んでいない",
                src: playableSrc,
              },
              enabled: playbackErrorLogEnabled,
            });
          }
        }, 3000);
      })
      .catch((e) => {
        console.warn("Play error:", e);
        setIsPlaying(false);
        logPlaybackIssue({
          event: "play_error",
          audio,
          user,
          track,
          extra: { message: String(e?.message || e), src: playableSrc },
          enabled: playbackErrorLogEnabled,
        });
      });
  };

  const toggleLoop = () => {
    const audio = audioRef.current;
    const next = !isLoop;
    setIsLoop(next);
    if (audio) audio.loop = next;
  };

  const toggleMeditationMode = () => setMeditationMode((prev) => !prev);

  // audio イベント（duration / ended / error / stalled / waiting）
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => setDuration(audio.duration || 0);
    const handleEnded = () => {
      if (!isLoop) setIsPlaying(false);
    };

    const handleError = () => {
      const mediaError = audio.error;
      logPlaybackIssue({
        event: "audio_error",
        audio,
        user,
        track,
        extra: {
          code: mediaError?.code || null,
          message: mediaError?.message || "unknown audio error",
          src: audio.currentSrc || playableSrc,
        },
        enabled: playbackErrorLogEnabled,
      });
    };

    const handleStalled = () => {
      logPlaybackIssue({
        event: "audio_stalled",
        audio,
        user,
        track,
        extra: { src: audio.currentSrc || playableSrc },
        enabled: playbackErrorLogEnabled,
      });
    };

    // waiting が5秒以上続いたらログ
    let waitingTimer = null;
    const handleWaiting = () => {
      if (waitingTimer) clearTimeout(waitingTimer);
      waitingTimer = setTimeout(() => {
        logPlaybackIssue({
          event: "audio_waiting_timeout",
          audio,
          user,
          track,
          extra: { src: audio.currentSrc || playableSrc, waitedSec: 5 },
          enabled: playbackErrorLogEnabled,
        });
      }, 5000);
    };
    const handlePlaying = () => {
      if (waitingTimer) { clearTimeout(waitingTimer); waitingTimer = null; }
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);
    audio.addEventListener("stalled", handleStalled);
    audio.addEventListener("waiting", handleWaiting);
    audio.addEventListener("playing", handlePlaying);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
      audio.removeEventListener("stalled", handleStalled);
      audio.removeEventListener("waiting", handleWaiting);
      audio.removeEventListener("playing", handlePlaying);
      if (waitingTimer) clearTimeout(waitingTimer);
    };
  }, [isLoop, track?.id, playableSrc, user, track, playbackErrorLogEnabled]);

  // ★ 暗転判定：4期は「更新中のみ明るい」、非4期は暗転させない（仕様維持）
  const listeningPanelActive = isPhase4 ? listeningActive : true;

  // ★ adminTimerChip も canSeeAdminUI のみ
  const adminTimerChip = canSeeAdminUI ? (
    <Chip
      label={
        derivedListening.shouldPoll && listeningActive
          ? `更新中 / 残り ${formatMMSS(listeningRemainSec)}`
          : "更新停止"
      }
      size="small"
      sx={{
        ml: 1.4,
        fontWeight: 800,
        borderRadius: 999,
        bgcolor:
          derivedListening.shouldPoll && listeningActive
            ? `${accent}12`
            : "rgba(0,0,0,0.06)",
        color:
          derivedListening.shouldPoll && listeningActive
            ? accent
            : "rgba(40,40,60,0.6)",
        border: `1px solid ${
          derivedListening.shouldPoll && listeningActive
            ? `${accent}33`
            : "rgba(0,0,0,0.10)"
        }`,
      }}
    />
  ) : null;

  return (
    <>
      {/* OtohikariGlobe（最上部） */}
      <OtohikariGlobe
        height={820}
        autoRotate
        showDebugControls={true}
        isAdmin={canSeeAdminUI}
        db={db}
        trackId={track?.id}
        aggregateCollectionName="musicDailyAggregates"
        pollSec={derivedListening.pollSec}
        burstMin={derivedListening.windowMin}
        currentListeners={currentListeners}
        todayListeners={todayListeners}
      />

      {/* メインプレーヤー */}
      <Box
        sx={{
          borderRadius: 4,
          px: { xs: 2.4, sm: 3 },
          py: { xs: 1.2, sm: 1.5 },
          mb: 2,
          position: "relative",
          overflow: "hidden",
          background: `linear-gradient(135deg, ${softBg} 0%, ${softBg} 60%, ${softBg}EE 100%)`,
          border: `1px solid ${border}`,
          boxShadow:
            "0 18px 45px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.7) inset",
          backgroundImage: `linear-gradient(135deg, ${accent}11 0%, ${accent}88 40%, ${softBg}FF 100%)`,
          backgroundSize: "260% 260%",
        }}
      >
        <Box sx={{ position: "relative", zIndex: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Typography
                  variant="overline"
                  sx={{
                    letterSpacing: "0.12em",
                    fontSize: 14,
                    color: textColor,
                    lineHeight: 1.3,
                  }}
                >
                  {track ? (() => {
                    const full = getDisplayTitle(track);
                    const m = full.match(/^(.+?)([\s　]*[（(].+[）)])$/);
                    if (m) {
                      return (
                        <>
                          <span style={{ fontWeight: 900, color: accent }}>⚡ {m[1]}</span>
                          <span style={{ opacity: 0.55, fontWeight: 400 }}>{m[2]}</span>
                        </>
                      );
                    }
                    return <span style={{ fontWeight: 900, color: accent }}>⚡ {full}</span>;
                  })() : "現在再生できる曲はありません"}
                </Typography>
                {canSeeAdminUI && (
                  <Typography
                    component="button"
                    onClick={() => setShowDlInfo((v) => !v)}
                    sx={{
                      background: showDlInfo ? `${accent}22` : "transparent",
                      border: showDlInfo ? `1px solid ${accent}44` : `1px solid ${border}`,
                      borderRadius: 1.5,
                      px: 1,
                      py: 0.25,
                      fontSize: 10,
                      fontWeight: showDlInfo ? 700 : 400,
                      color: showDlInfo ? accent : textColor,
                      opacity: showDlInfo ? 1 : 0.5,
                      cursor: "pointer",
                      letterSpacing: "0.08em",
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      ml: 1,
                    }}
                  >
                    DL INFO
                  </Typography>
                )}
              </Box>

              {/* 音源準備（全量DL） */}
              {networkSrc && dlState.status === "downloading" && (
                <Box
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    mb: 1,
                    columnGap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      border: `2px solid ${accent}`,
                      borderTopColor: "transparent",
                      animation: "spinMM 0.8s linear infinite",
                      "@keyframes spinMM": {
                        "0%": { transform: "rotate(0deg)" },
                        "100%": { transform: "rotate(360deg)" },
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.78, fontSize: 11 }}
                  >
                    {dlState.message}
                    {dlState.total ? ` ${dlState.progress}%` : ""}
                  </Typography>
                </Box>
              )}

              {networkSrc && dlState.status === "error" && (
                <Typography
                  variant="caption"
                  sx={{ opacity: 0.85, fontSize: 11 }}
                >
                  {dlState.message}
                </Typography>
              )}

              {/* progress + 操作ボタンを横並び */}
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <PlayerProgress
                    audioRef={audioRef}
                    canPlay={canPlay}
                    duration={duration}
                    accent={accent}
                    textColor={textColor}
                  />
                </Box>

                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                  <IconButton
                    onClick={togglePlay}
                    disabled={!canPlay || loadingTrack}
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      bgcolor: accent,
                      boxShadow:
                        "0 6px 16px rgba(0,0,0,0.2), 0 0 0 3px rgba(255,255,255,0.9)",
                      color: "#fff",
                      "&:hover": { bgcolor: accent },
                      opacity: canPlay ? 1 : 0.5,
                    }}
                  >
                    {isPlaying ? (
                      <PauseRoundedIcon sx={{ fontSize: 22 }} />
                    ) : (
                      <PlayArrowRoundedIcon sx={{ fontSize: 22 }} />
                    )}
                  </IconButton>

                  <Badge
                    overlap="circular"
                    badgeContent={isLoop ? "ON" : null}
                    sx={{
                      "& .MuiBadge-badge": {
                        fontSize: 10,
                        fontWeight: 900,
                        borderRadius: 999,
                        px: 0.8,
                        height: 18,
                        minWidth: 18,
                        bgcolor: accent,
                        color: "#fff",
                        boxShadow: "0 8px 18px rgba(0,0,0,0.25)",
                      },
                    }}
                  >
                    <IconButton
                      onClick={toggleLoop}
                      disabled={!canPlay}
                      sx={{
                        borderRadius: 2,
                        color: isLoop ? accent : textColor,
                        bgcolor: isLoop
                          ? `${accent}22`
                          : "rgba(255,255,255,0.25)",
                        border: isLoop
                          ? `2px solid ${accent}`
                          : "1px solid rgba(0,0,0,0.10)",
                        transform: isLoop ? "scale(1.06)" : "scale(1)",
                        transition:
                          "transform 120ms ease, background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease",
                        boxShadow: isLoop ? `0 0 0 4px ${accent}22` : "none",
                        "&:hover": {
                          bgcolor: isLoop ? `${accent}28` : "rgba(0,0,0,0.04)",
                        },
                        "&.Mui-disabled": { opacity: 0.4 },
                      }}
                    >
                      <LoopRoundedIcon fontSize="small" />
                    </IconButton>
                  </Badge>

                  <IconButton
                    onClick={toggleMeditationMode}
                    disabled={!canPlay}
                    sx={{
                      color: meditationMode ? accent : textColor,
                      bgcolor: meditationMode ? `${accent}18` : "transparent",
                      borderRadius: 2,
                    }}
                  >
                    <SelfImprovementRoundedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              {/* 管理者だけ：DL情報（Globe上のスイッチで切替） */}
              {canSeeAdminUI && showDlInfo && dlState.status === "ready" && (
                <Box sx={{ mt: 0.5, pl: 0.5 }}>
                  <Typography variant="caption" sx={{ opacity: 0.78 }}>
                    ダウンロード完了
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", opacity: 0.72 }}
                  >
                    {adminDlFileName}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", opacity: 0.65 }}
                  >
                    {formatMB(adminDlSizeBytes)}
                  </Typography>
                </Box>
              )}
            </Box>
        </Box>

        {/* 星粒（停止） */}
        <DotsCanvas active={false} />

        {/* audio（ローカルBlobのみ） */}
        {playableSrc && (
          <audio
            ref={audioRef}
            src={playableSrc}
            onLoadedMetadata={(e) => {
              const d = e?.currentTarget?.duration || 0;
              setDuration(Number.isFinite(d) ? d : 0);
            }}
            onEnded={() => {
              if (!isLoop) setIsPlaying(false);
            }}
          />
        )}
      </Box>
      {/* listening status — 一時非表示 */}
      {/* <MusicListeningStats
        currentListeners={currentListeners}
        todayListeners={todayListeners}
        accent={accent}
        border={border}
        textColor={textColor}
        softBg={softBg}
        isActive={listeningPanelActive}
        adminTimer={adminTimerChip}
        debugInfo={canSeeAdminUI && ENABLE_LISTENING_DEBUG ? lsDebug : null}
      /> */}
      {/* {canSeeAdminUI ? <CommunityFeedNoImage /> : null} */}
      {canSeeCommunityFeed ? <CommunityFeedNoImage /> : null}
      {/* 柱マップ */}
      {track?.id && (
        <MusicGeoPrefectureStats
          trackId={track.id}
          accentColor={accent}
          lowPower={false}
          refreshToken={mapRefreshToken}
        />
      )}
      {/* 権限なしダイアログ */}
      <Dialog
        open={permissionDialogOpen}
        onClose={() => setPermissionDialogOpen(false)}
      >
        <DialogTitle>参加権限がありません</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            お支払いが終わると、音源の視聴が出来ます。
          </Typography>
          <Typography variant="body2">
            お支払いは、ホーム画面の右上にある2026というリボンマークを押して下さい。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDialogOpen(false)}>閉じる</Button>
        </DialogActions>
      </Dialog>
      {/* 瞑想モード黒画面 */}
      {meditationMode && (
        <Box
          onClick={() => setMeditationMode(false)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.98)",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            textAlign: "center",
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            瞑想モード中…
          </Typography>

          <Typography variant="body2" sx={{ opacity: 0.7, mb: 2 }}>
            画面をタップすると、通常モードに戻ります。
          </Typography>

          <Typography
            variant="caption"
            sx={{
              opacity: 0.55,
              maxWidth: 260,
              lineHeight: 1.5,
              textAlign: "center",
            }}
          >
            このモードでは画面の暗転とスリープ防止を試みていますが、一部の端末やブラウザの
            バージョンでは、自動スリープが完全には抑制されない場合があります。
          </Typography>
        </Box>
      )}
    </>
  );
}

// ─────────────────────────────
// Progress（親の再描画から隔離する）
// ─────────────────────────────
const PlayerProgress = React.memo(function PlayerProgress({
  audioRef,
  canPlay,
  duration,
  accent,
  textColor,
}) {
  const [currentTime, setCurrentTime] = useState(0);

  // setState頻度制御
  const lastSetMsRef = useRef(0);
  const lastTimeRef = useRef(0);
  const rafRef = useRef(null);

  const formatTime = (sec) => {
    if (!sec || Number.isNaN(sec)) return "0:00";
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60)
      .toString()
      .padStart(2, "0");
    return `${m}:${s}`;
  };

  useEffect(() => {
    const loop = () => {
      const a = audioRef.current;
      if (a && canPlay) {
        const t = a.currentTime || 0;
        const prev = lastTimeRef.current;
        const now = performance.now();

        if (now - lastSetMsRef.current >= 80 || Math.abs(t - prev) >= 0.25) {
          lastSetMsRef.current = now;
          lastTimeRef.current = t;
          setCurrentTime(t);
        }
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [audioRef, canPlay]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleSeek = (_, value) => {
    const a = audioRef.current;
    if (!a || !duration) return;

    const newTime = (value / 100) * duration;
    a.currentTime = newTime;

    lastTimeRef.current = newTime;
    setCurrentTime(newTime);
  };

  return (
    <Box sx={{ mt: 0.3 }}>
      <Slider
        size="small"
        value={progress}
        onChange={handleSeek}
        aria-label="progress"
        disabled={!canPlay || duration <= 0}
        sx={{
          "& .MuiSlider-rail": { opacity: 0.3 },
          "& .MuiSlider-track": {
            border: "none",
            background: `linear-gradient(90deg, ${accent}, ${accent}AA)`,
          },
          "& .MuiSlider-thumb": {
            width: 14,
            height: 14,
            boxShadow: "0 0 0 4px rgba(255,255,255,0.9)",
          },
        }}
      />
      <Stack direction="row" justifyContent="space-between" sx={{ mt: -0.3 }}>
        <Typography variant="caption" sx={{ opacity: 0.8, color: textColor }}>
          {formatTime(currentTime)}
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.8, color: textColor }}>
          {formatTime(duration)}
        </Typography>
      </Stack>
    </Box>
  );
});

// ─────────────────────────────
// 「いま / 今日 聴いている人」表示用パネル
// ─────────────────────────────
function MusicListeningStats({
  currentListeners,
  todayListeners,
  accent,
  border,
  textColor,
  softBg,
  isActive,
  adminTimer,
  debugInfo,
}) {
  const safeNum = (v) => (typeof v === "number" && !Number.isNaN(v) ? v : 0);

  return (
    <Box
      sx={{
        mb: 3,
        borderRadius: 3,
        p: 1.5,
        border: `1px dashed ${border}`,
        bgcolor: softBg,
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
        rowGap: 1,

        // 停止時は暗く
        opacity: isActive ? 1 : 0.62,
        filter: isActive ? "none" : "saturate(0.85)",
        transition: "opacity 240ms ease, filter 240ms ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", columnGap: 1 }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: textColor,
            opacity: 0.7,
          }}
        >
          LISTENING STATUS
        </Typography>
        {adminTimer}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2.5,
          mt: 0.5,
          width: "100%",
        }}
      >
        <Box>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, fontSize: 12, mb: 0.3 }}
          >
            今この瞬間に聴いている人
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: accent, lineHeight: 1.1 }}
          >
            {safeNum(currentListeners)}
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 0.5, opacity: 0.7 }}
            >
              人
            </Typography>
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="body2"
            sx={{ opacity: 0.7, fontSize: 12, mb: 0.3 }}
          >
            今日『音』を繋いだ人数
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: 800, color: textColor, lineHeight: 1.1 }}
          >
            {safeNum(todayListeners)}
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 0.5, opacity: 0.7 }}
            >
              人
            </Typography>
          </Typography>
        </Box>
      </Box>

      {/* 管理者のみ：Listening Debug */}
      {/* {debugInfo && (
        <Box
          sx={{
            mt: 1.1,
            width: "100%",
            borderRadius: 2,
            p: 1,
            bgcolor: "rgba(0,0,0,0.035)",
            border: "1px solid rgba(0,0,0,0.06)",
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
            fontSize: 11,
            lineHeight: 1.55,
            color: "rgba(40,40,60,0.85)",
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {JSON.stringify(debugInfo, null, 2)}
        </Box>
      )} */}
    </Box>
  );
}

// ─────────────────────────────
// 星粒（停止）
// ─────────────────────────────
function DotsCanvas({ active }) {
  const canvasRef = useRef(null);
  const activeRef = useRef(active);
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isAndroid = /Android/i.test(ua);
    setEnabled(!isAndroid);
  }, []);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    if (!enabled) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let frameId;
    const dpr = window.devicePixelRatio || 1;
    let width = 0;
    let height = 0;

    const NUM = 120;
    let stars = [];
    let destroyed = false;

    const initStars = () => {
      stars = Array.from({ length: NUM }).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: 0.7 + Math.random() * 1.0,
        life: Math.random(),
        lifeSpeed: 0.003 + Math.random() * 0.004,
      }));
    };

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      width = rect.width || 0;
      height = rect.height || 0;
      if (width === 0 || height === 0) return;

      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      initStars();
    };

    resize();
    window.addEventListener("resize", resize);

    const render = () => {
      if (destroyed) return;

      try {
        ctx.clearRect(0, 0, width, height);

        if (activeRef.current && width > 0 && height > 0) {
          for (const s of stars) {
            s.x += s.vx;
            s.y += s.vy;

            if (s.x < -10) s.x = width + 10;
            if (s.x > width + 10) s.x = -10;
            if (s.y < -10) s.y = height + 10;
            if (s.y > height + 10) s.y = -10;

            s.life += s.lifeSpeed;
            if (s.life > 1) {
              s.life = 0;
              s.x = Math.random() * width;
              s.y = Math.random() * height;
              s.vx = (Math.random() - 0.5) * 0.25;
              s.vy = (Math.random() - 0.5) * 0.25;
            }

            const alpha = Math.sin(s.life * Math.PI);
            ctx.globalAlpha = alpha * 0.9;
            ctx.beginPath();
            ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
            ctx.fillStyle = "#FFFFFF";
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        }
      } catch (e) {
        console.error("DotsCanvas render error:", e);
      }

      frameId = requestAnimationFrame(render);
    };

    frameId = requestAnimationFrame(render);

    return () => {
      destroyed = true;
      if (frameId) cancelAnimationFrame(frameId);
      window.removeEventListener("resize", resize);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 1,
      }}
    />
  );
}
